<template>
  <v-dialog v-model="openModal" @click:outside="handleCloseModal" width="608px">
    <v-card class="v-card__modal">
      <v-toolbar elevation="0" color="background">
        <v-toolbar-title
          class="text-h6 font-weight-medium pl-4"
          style="align-self: flex-start"
        >
          {{ $t('message.downloadVideoModal.title') }}
          <p class="text-subtitle__modal mb-0">
            {{ videoName }}
          </p>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items class="align-self-baseline">
          <slot name="beforeCloseBtn"> </slot>
          <v-btn
            icon
            @click="handleCloseModal"
            class="no-ripple btn-close__modal pr-4"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-list class="original-lang__list">
        <v-list-item class="border-divider__modal">
          <v-list-item-avatar v-if="originalLang" class="avatar-flag__modal">
            <FlagCards :items="[getLangCodeBySrcLang(originalLang).country]" />
          </v-list-item-avatar>
          <v-list-item-avatar v-else class="avatar-flag__modal">
            <simple-svg
              :src="globeIcon"
              width="16px"
              height="16px"
              fill-class-name="fill-color"
              :fill="$vuetify.theme.dark ? '#757BA3' : '#8F92A3'"
              style="margin-left: 6px"
            />
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="text-file__modal">{{
              $t('message.downloadVideoModal.originalLang')
            }}</v-list-item-title>
            <v-list-item-subtitle
              v-if="originalLang"
              class="text-subtitle__modal"
              >{{
                getLangCodeBySrcLang(originalLang).label
              }}</v-list-item-subtitle
            >
          </v-list-item-content>
          <v-list-item-action class="d-flex flex-row">
            <base-select
              :v-model="originalAudioSelectedQuality"
              :items="originalAudioVideoQualities"
              :value="originalAudioDefaultQuality"
              @change="handleSelectedOption($event)"
              size="medium"
              style="width: 169px; margin-right: 12px !important"
              dense
              hide-details
            />
            <base-btn
              v-if="!originalAudioDownloadDisabled"
              style="border-radius: 4px !important"
              :style="locale === 'pt' ? 'min-width: 100px' : 'min-width: 126px'"
              :class="{
                'no-pointer-events': originalAudioDownloadLoading,
              }"
              icon
              size="default"
              @click="downloadOriginalAudio()"
              type="tertiary"
              :loading="originalAudioDownloadLoading"
            >
              <span
                class="responsive-text d-flex"
                style="justify-content: center !important"
              >
                {{ $t('message.download') }}
                <simple-svg
                  v-if="!originalAudioDownloadDisabled"
                  :src="downloadIcon"
                  width="16px"
                  height="16px"
                  fill-class-name="fill-color"
                  :fill="$vuetify.theme.dark ? '#C2C5D6' : '#3F404C'"
                />
              </span>
            </base-btn>
            <v-tooltip v-else max-width="200px" top>
              <template v-slot:activator="{ on, attrs }">
                <base-btn
                  v-bind="attrs"
                  v-on="{ ...on, ...$listeners }"
                  class="disabled-button"
                  :style="
                    locale === 'pt' ? 'min-width: 100px' : 'min-width: 126px'
                  "
                  size="default"
                  type="tertiary"
                >
                  <span
                    class="responsive-text d-flex"
                    style="justify-content: center !important"
                  >
                    {{ $t('message.download') }}
                    <simple-svg
                      :src="downloadIcon"
                      width="16px"
                      height="16px"
                      fill-class-name="fill-color"
                      :fill="$vuetify.theme.dark ? '#C2C5D6' : '#3F404C'"
                    />
                  </span>
                </base-btn>
              </template>
              <span>{{ $t('message.downloadVideoModal.awaitDownload') }}</span>
            </v-tooltip>
          </v-list-item-action>
        </v-list-item>
      </v-list>
      <v-list class="audio-files__list">
        <v-list-item
          class="border-divider__modal"
          v-for="(option, index) in availableAudios"
          :key="`${option.lang}-${index}`"
        >
          <v-list-item-avatar class="avatar-flag__modal">
            <FlagCards
              v-if="option && option.lang"
              :items="[handleLangCodeBySrcLang(option).country]"
            />
            <simple-svg
              v-else
              :src="globeIcon"
              width="16px"
              height="16px"
              fill-class-name="fill-color"
              :fill="$vuetify.theme.dark ? '#757BA3' : '#8F92A3'"
              style="margin-left: 6px"
            />
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="text-file__modal">{{
              formatLabel(option.type)
            }}</v-list-item-title>
            <v-list-item-subtitle
              v-if="option && option.lang"
              class="text-subtitle__modal"
              >{{ handleLangCodeBySrcLang(option).label }}</v-list-item-subtitle
            >
          </v-list-item-content>
          <v-list-item-action class="d-flex flex-row">
            <base-select
              :v-model="dubbedAudioSelectedQuality[index]"
              :items="dubbedAudioVideoQualities"
              :value="dubbedAudioDefaultQuality"
              @change="handleSelectedQuality($event, index)"
              dense
              hide-details
              style="width: 169px; margin-right: 12px !important"
            ></base-select>
            <base-btn
              v-if="!dubbedAudioDownloadDisabled[index]"
              style="border-radius: 4px !important"
              :style="locale === 'pt' ? 'min-width: 100px' : 'min-width: 126px'"
              :class="{
                'no-pointer-events': dubbedAudioDownloadLoading[index],
              }"
              icon
              size="default"
              @click="downloadDubbedAudio(index, option)"
              type="tertiary"
              :loading="dubbedAudioDownloadLoading[index]"
            >
              <span
                class="responsive-text d-flex"
                style="justify-content: center !important"
              >
                {{ $t('message.download') }}
                <simple-svg
                  :src="downloadIcon"
                  width="16px"
                  height="16px"
                  fill-class-name="fill-color"
                  :fill="$vuetify.theme.dark ? '#C2C5D6' : '#3F404C'"
                />
              </span>
            </base-btn>
            <v-tooltip v-else max-width="200px" top>
              <template v-slot:activator="{ on, attrs }">
                <base-btn
                  v-bind="attrs"
                  v-on="{ ...on, ...$listeners }"
                  class="disabled-button"
                  :style="
                    locale === 'pt' ? 'min-width: 100px' : 'min-width: 126px'
                  "
                  size="default"
                  type="tertiary"
                >
                  <span
                    class="responsive-text d-flex"
                    style="justify-content: center !important"
                  >
                    {{ $t('message.download') }}
                    <simple-svg
                      :src="downloadIcon"
                      width="16px"
                      height="16px"
                      fill-class-name="fill-color"
                      :fill="$vuetify.theme.dark ? '#C2C5D6' : '#3F404C'"
                    />
                  </span>
                </base-btn>
              </template>
              <span>{{ $t('message.downloadVideoModal.awaitDownload') }}</span>
            </v-tooltip>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-card>
  </v-dialog>
</template>
<script>
import FlagCards from '@/components/FlagCards.vue'
import BaseBtn from '@/components/BaseBtn.vue'
import BaseSelect from '@/components/BaseComponents/BaseSelect.vue'
import langCodes from '@/utils/langCodes.mixin'
import downloadUrl from '@/utils/downloadUrl.mixin'
import i18n from '@/translations/lang'
import { mapMutations } from 'vuex'

import globeIcon from '@/assets/pandaIcons/globe.svg'
import downloadIcon from '@/assets/pandaIcons/download.svg'

export default {
  name: 'DownloadVideoModal',
  components: { FlagCards, BaseBtn, BaseSelect },
  mixins: [langCodes, downloadUrl],
  data() {
    return {
      openModal: null,
      loadingControl: null,
      dubbedAudioSelectedQuality: [],
      dubbedAudioDownloadLoading: [],
      dubbedAudioDownloadDisabled: [],
      dubbedAudioDefaultQuality: '',
      dubbedAudioVideoQualities: [],
      originalAudioSelectedQuality: '',
      originalAudioDownloadLoading: false,
      originalAudioVideoQualities: [],
      originalAudioDefaultQuality: 'Original',
      originalAudioDownloadDisabled: false,
      globeIcon,
      downloadIcon,
    }
  },
  props: {
    openDownloadVideoModal: {
      type: Boolean,
      default: false,
    },
    videoName: {
      type: String,
      default: '',
    },
    videoId: {
      type: String,
      default: '',
    },
    originalLang: {
      type: String,
      default: '',
    },
    availableAudios: {
      type: Array,
    },
    availableVideoQualities: {
      type: Array,
    },
    downloadLoadingStatus: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    locale() {
      return this.$i18next.resolvedLanguage
    },
  },
  watch: {
    openDownloadVideoModal(val) {
      this.openModal = val
    },
    downloadLoadingStatus(val) {
      if (this.loadingControl === 'backup') {
        if (val) {
          setTimeout(() => {
            this.originalAudioDownloadLoading = !val
            this.originalAudioDownloadDisabled = val
            this.updateSnackBar()
          }, 3000)
        }
        return (this.originalAudioDownloadLoading = val)
      }
      this.$set(this.dubbedAudioDownloadLoading, this.loadingControl, val)
      if (val) {
        setTimeout(() => {
          this.$set(this.dubbedAudioDownloadLoading, this.loadingControl, !val)
          this.$set(this.dubbedAudioDownloadDisabled, this.loadingControl, val)
          this.updateSnackBar()
        }, 3000)
      }
    },
    availableVideoQualities(qualitiesArray) {
      this.originalAudioVideoQualities = qualitiesArray.concat([
        this.originalAudioDefaultQuality,
      ])
      this.dubbedAudioVideoQualities = qualitiesArray
      this.dubbedAudioDefaultQuality = qualitiesArray[0]
    },
    availableAudios(val) {
      this.dubbedAudioDownloadLoading = val.map(() => false)
      this.dubbedAudioDownloadDisabled = val.map(() => false)
    },
  },
  async mounted() {
    this.$root.$emit('closeDownloadVideoModal')
  },
  beforeDestroy() {
    this.$root.$off('openDownloadVideoModal')
    this.$root.$off('closeDownloadVideoModal')
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    handleCloseModal() {
      this.dubbedAudioDownloadLoading = this.availableAudios.map(() => false)
      this.dubbedAudioDownloadDisabled = this.availableAudios.map(() => false)
      this.$root.$emit('closeDownloadVideoModal')
    },
    async downloadDubbedAudio(index, option) {
      this.loadingControl = index

      const qualityToDownload =
        this.dubbedAudioSelectedQuality[index] ?? this.dubbedAudioDefaultQuality

      const formattedDownloadUrl = this.downloadDubUrl(
        this.videoId,
        qualityToDownload,
        option.lang
      )
      this.$root.$emit('handleVideoDownload', formattedDownloadUrl)
    },
    downloadOriginalAudio() {
      this.loadingControl = 'backup'
      const selectedQuality =
        this.originalAudioSelectedQuality ?? this.originalAudioDefaultQuality

      const qualityToDownload =
        selectedQuality === 'Original' ? '' : selectedQuality

      const formattedDownloadUrl = this.downloadDubUrl(
        this.videoId,
        qualityToDownload
      )
      this.$root.$emit('handleVideoDownload', formattedDownloadUrl)
    },
    formatLabel(type) {
      if (type === 'GENERATED')
        return i18n.t('message.downloadVideoModal.dubbingPanda')
      if (type === 'UPLOADED')
        return i18n.t('message.downloadVideoModal.audioFile')
    },
    handleLangCodeBySrcLang(option) {
      const lang = option.lang === 'pt' ? 'pt-BR' : option.lang
      return this.getLangCodeBySrcLang(lang)
    },
    handleSelectedQuality(val, index) {
      this.dubbedAudioSelectedQuality[index] = val
      this.$set(this.dubbedAudioDownloadDisabled, index, false)
    },
    handleSelectedOption(val) {
      this.originalAudioSelectedQuality = val
      this.originalAudioDownloadDisabled = false
    },
    updateSnackBar() {
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: i18n.t('message.downloadVideoModal.awaitDownload'),
        type: 'info',
      })
    },
  },
}
</script>
<style lang="scss">
.text-subtitle__modal {
  color: varFigma(neutral-40-light) !important;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
}
.text-file__modal {
  color: varFigma(neutral-20-dark) !important;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
}
.avatar-flag__modal {
  scale: 1.3;
  min-width: unset !important;
  width: unset !important;
}
.v-toolbar__content {
  padding: 0;
}
.v-card__modal {
  padding: 24px 16px;
}
.btn-close__modal {
  justify-content: flex-end !important;
  align-items: flex-start !important;
  padding-top: 4px !important;
}
.v-list-item__action.d-flex.flex-row.v-list-item__action--stack {
  align-items: center !important;
}
.original-lang__list {
  padding-bottom: 0;
}
.audio-files__list {
  padding-top: 0;
}
.disabled-button {
  border-radius: 4px !important;
  cursor: not-allowed !important;
  opacity: 0.3;
}
.no-pointer-events {
  pointer-events: none !important;
}
.responsive-text.d-flex svg {
  margin-left: 6px !important;
  align-self: center !important;
}
</style>
